import { athenaQuery } from './AthenaApi';
import { LocaleData } from './GetLocalizedMovies';

export type Movie = {
  id: string;
  originalTitle: string;
  originalPoster: string;
  en_GB: LocaleData;
  fallback: LocaleData;
  mainRelease: {
    certificate: string;
  };
};

export type Showtime = {
  startsAt: string;
  tags: string[];
  data: {
    ticketing: Array<{
      urls: string[];
    }>;
  };
};

export type getFilmsAndShowtimesParameters = {
  cinemaId: string;
  fromDate: string;
  toDate: string;
  showtimeTags: string[];
};

export type getFilmsAndShowtimesResult = {
  movie: Movie;
  showtimes: Showtime[];
};

// --- Athena -> legacy shape mappers ---

// Athena movie fields differ from the old Webedia shape (poster is `{ url }`, the
// locale is `locale(...)`, certificate comes from `releases[].rating`). Map back to
// the legacy Movie so getBestTitle/getBestPoster and the certificate read are unchanged.
// There is no en_US locale; the top-level title/poster are the fallback, already
// captured as originalTitle/originalPoster (which getBestTitle/getBestPoster use last).
export const mapAthenaMovie = (movie: any): Movie => {
  const locale = movie.locale || {};
  const releases = movie.releases || [];
  return {
    id: movie.id,
    originalTitle: movie.title || '',
    originalPoster: movie.poster && movie.poster.url ? movie.poster.url : '',
    en_GB: {
      title: locale.title || '',
      synopsis: locale.synopsis || '',
      poster: locale.poster && locale.poster.url ? locale.poster.url : '',
    },
    fallback: { title: '', synopsis: '', poster: '' },
    mainRelease: {
      certificate:
        releases[0] && releases[0].rating ? releases[0].rating.certificate : '',
    },
  };
};

// Map Athena showtimes into the legacy Showtime shape so formatShowtimes (which reads
// `tags` and `data.ticketing[0].urls[0]`) and the per-showtime icons are unchanged.
// `attributes` carries the dotted accessibility tags; `ticketingUrl` is the [String]
// whose first entry is the booking link.
export const mapAthenaShowtimes = (showtimes: any[]): Showtime[] =>
  (showtimes || []).map((showtime: any) => ({
    startsAt: showtime.startsAt,
    tags: showtime.attributes || [],
    data: {
      ticketing:
        showtime.ticketingUrl && showtime.ticketingUrl.length > 0
          ? [{ urls: showtime.ticketingUrl }]
          : [],
    },
  }));

// Shared movie selection set (same in both showtime queries).
export const MOVIE_FIELDS = `
  id
  title
  poster { url }
  locale(locale: "en_GB") { title synopsis poster { url } }
  releases(iso_3166_1_a2: "GB") { rating { certificate } }
`;

// Build the `<arg>: { in: [...] }` accessibility filter fragment (or '' when none).
export const buildTagsFilter = (argName: string, tags: string[]): string =>
  tags.length > 0
    ? `${argName}: { in: [${tags.map((tag) => `"${tag}"`).join(',')}] }`
    : '';

export const getFilmsAndShowtimes = (
  parameters: getFilmsAndShowtimesParameters
): Promise<Array<getFilmsAndShowtimesResult>> => {
  const tagsFilter = buildTagsFilter('tags', parameters.showtimeTags);

  const query = `
    query GetFilmsAndShowtimes {
      theater(id: "${parameters.cinemaId}") {
        schedule(
          from: "${parameters.fromDate}",
          to: "${parameters.toDate}"
          ${tagsFilter ? `, ${tagsFilter}` : ''}
        ) {
          nodes {
            movie {
              ${MOVIE_FIELDS}
            }
            showtimes {
              startsAt
              attributes
              ticketingUrl
            }
          }
        }
      }
    }`;

  return new Promise((resolve) => {
    athenaQuery(query, 'theater')
      .then((r: any) => {
        if (r === null || r.data.theater.schedule.nodes.length === 0) {
          resolve([]);
          return;
        }

        resolve(
          r.data.theater.schedule.nodes.map((node: any) => ({
            movie: mapAthenaMovie(node.movie),
            showtimes: mapAthenaShowtimes(node.showtimes),
          }))
        );
      })
      .catch(() => {
        resolve([]);
      });
  });
};
